import { useEffect } from "react"
import { X } from "lucide-react"

export interface SaveResultToastItem {
  id: string
  order_number: string
  pickup_datetime: string
  label: string // 例如 "去程" / "回程" / ""（單筆訂單時不顯示）
}

interface SaveResultToastProps {
  items: SaveResultToastItem[]
  onOpen: (id: string) => void
  onClose: () => void
  /** 自動關閉毫秒數，預設 3000，傳 0 停用自動關閉 */
  autoCloseMs?: number
}

/** 派車單新增/編輯儲存成功後的浮動提示，可直接點按鈕開啟剛儲存的訂單 */
export default function SaveResultToast({ items, onOpen, onClose, autoCloseMs = 3000 }: SaveResultToastProps) {
  useEffect(() => {
    if (!autoCloseMs) return
    const timer = setTimeout(onClose, autoCloseMs)
    return () => clearTimeout(timer)
  }, [items, autoCloseMs, onClose])

  if (items.length === 0) return null

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso)
      return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    } catch {
      return iso
    }
  }

  return (
    <div className="fixed top-4 right-4 z-[70] w-[calc(100%-2rem)] max-w-sm rounded-lg border border-green-200 bg-white dark:bg-gray-800 dark:border-green-900 shadow-lg overflow-hidden animate-in slide-in-from-top-2">
      <div className="flex items-center justify-between px-3 py-2 bg-green-50 dark:bg-green-900/30 border-b border-green-100 dark:border-green-900">
        <span className="text-sm font-bold text-green-700 dark:text-green-400">
          {items.length > 1 ? "訂單已儲存（含來回程）" : "訂單已儲存"}
        </span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer" aria-label="關閉">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-2 px-3 py-2">
            <div className="min-w-0">
              {item.label && (
                <span className="inline-block mr-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                  {item.label}
                </span>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(item.pickup_datetime)}</span>
              <span className="ml-1.5 text-xs text-gray-400" title={item.order_number}>#{item.order_number.slice(-4)}</span>
            </div>
            <button
              onClick={() => onOpen(item.id)}
              className="shrink-0 text-xs font-bold text-blue-600 hover:text-blue-800 border border-blue-200 hover:bg-blue-50 dark:border-blue-800 dark:hover:bg-blue-900/30 rounded px-2 py-1 cursor-pointer transition-colors"
            >
              開啟
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
